import { FadeLoader } from "react-spinners";
import { useIsDarkTheme } from "../../business/hooks/useIsDarkTheme";

/**
 * Props:
 * - variant: 'inline' | 'page' | 'overlay'
 * - size:    'sm' | 'md' | 'lg'
 * - text:    optional label below the spinner
 * - show:    boolean (default true)
 * - color:   override spinner color (otherwise auto from theme)
 * - className/style: wrapper props
 * - backdrop: boolean for overlay dim (default true for overlay)
 */
export default function Loader({
    variant = "inline",
    size = "md",
    text,
    show = true,
    color,
    className = "",
    style,
    backdrop = true,
}) {

    const isDark = useIsDarkTheme();
    if (!show) return null;
    const sizes = {
        sm: { height: 6, width: 3, radius: 1, margin: 2 },
        md: { height: 8, width: 4, radius: 2, margin: 3 },
        lg: { height: 10, width: 5, radius: 3, margin: 4 },
    };
    const { height, width, radius, margin } = sizes[size] ?? sizes.md;

    const getThemeColor = (varName, fallback) => {
        if (typeof window === "undefined") return fallback;
        const computed = getComputedStyle(document.documentElement).getPropertyValue(varName);
        return computed?.trim() || fallback;
    };
    const spinnerColor =
        color ||
        (isDark
            ? getThemeColor("--bs-white", "#ffffff")
            : getThemeColor("--bs-body-color", "#111827"));

    const baseFlex = "d-flex align-items-center justify-content-center";
    const wrapperClasses =
        variant === "page"
            ? `${baseFlex} vh-100 vw-100`
            : variant === "overlay"
                ? `${baseFlex} position-absolute top-0 start-0 w-100 h-100`
                : `${baseFlex}`;

    const wrapperStyle =
        variant === "overlay"
            ? {
                zIndex: 1050,
                background: backdrop
                    ? "color-mix(in oklab, var(--bs-body-color) 12%, transparent)"
                    : "transparent",
                backdropFilter: backdrop ? "blur(2px)" : undefined,
                WebkitBackdropFilter: backdrop ? "blur(2px)" : undefined,
                ...style,
            }
            : { ...style };

    return (
        <div
            className={`${wrapperClasses} ${className}`}
            style={wrapperStyle}
            role="status"
            aria-live="polite"
            aria-busy="true"
        >
            <div className="d-flex flex-column align-items-center">
                <FadeLoader
                    color={spinnerColor}
                    loading
                    height={height}
                    width={width}
                    radius={radius}
                    margin={margin}
                    speedMultiplier={0.9}
                    cssOverride={{ display: "block" }}
                />
                {text ? (
                    <small className="mt-2 text-body-secondary">{text}</small>
                ) : null}
            </div>
        </div>
    );
}
